'use client';

import { CarouselItem } from 'ui/components/Carousel/Carousel';
import { useState, useEffect, useRef } from 'react';
import { CarouselWithDots } from 'ui/components/CarouselWithDots/CarouselWithDots';
import { AmplitudeEvents, AmplitudeActions } from 'whoop-analytics/lib/types';
import { useTranslation } from '@whoop/i18n/lang/client';
import { NextGenerationTile } from './NextGenerationTile/NextGenerationTile';
import type { ImageCarouselFeatureItem } from '@/client/whoopDotComContentfulClient';
import NextGenerationModal from '@/components/OPLComponents/NextGenerationCarousel/NextGenerationModal/NextGenerationModal';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { useAnalytics } from '@/hooks/analytics';
interface NextGenerationCarouselProps {
  items: ImageCarouselFeatureItem[];
}
export function NextGenerationCarousel({
  items
}: NextGenerationCarouselProps) {
  const {
    trackAmplitudeEvent
  } = useAnalytics();
  const {
    t
  } = useTranslation('membershipTiers');
  const [showNexGenModal, setShowNextGenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(items?.[0]);
  const [lastTrackedIndex, setLastTrackedIndex] = useState<number | null>(null);

  // tracks if this is the initial render to avoid firing analytic on page load
  const initialRenderRef = useRef(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    if (currentIndex !== 0 && currentIndex !== lastTrackedIndex && items[currentIndex]) {
      const currentItem = items[currentIndex];
      trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
        action: AmplitudeActions.ProductFeatureCarouselScroll,
        category: currentItem?.setAHeading
      });
      setLastTrackedIndex(currentIndex);
    }
  }, [currentIndex, items, lastTrackedIndex, trackAmplitudeEvent]);
  if (!items?.length) return null;
  const handleSelection = (item: ImageCarouselFeatureItem) => {
    setSelectedItem(item);
    setShowNextGenModal(true);
    trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
      action: AmplitudeActions.ProductFeatureCarouselModalOpen,
      category: item.setASubheading
    });
  };

  // This function now just updates the state, the tracking happens in the useEffect
  const handleSlideChange = (index: number) => {
    setCurrentIndex(index);
  };
  return <>
      <NextGenerationModal isOpen={showNexGenModal} selectedItem={selectedItem} setIsOpen={setShowNextGenModal} data-sentry-element="NextGenerationModal" data-sentry-source-file="NextGenerationCarousel.tsx" />
      <div className='w-full items-center justify-center overflow-x-hidden bg-white pb-2 pl-6 pt-8 md:flex md:pl-0'>
        <div className='flex w-full flex-col md:max-w-screen-lg'>
          <div className='w-full'>
            <SectionHeader data-sentry-element="SectionHeader" data-sentry-source-file="NextGenerationCarousel.tsx">{t('explore-the-features')}</SectionHeader>
          </div>
          <div className='w-full  py-6 pr-6'>
            <CarouselWithDots setIndexFocused={handleSlideChange} showSneakPeek startIndex={0} data-sentry-element="CarouselWithDots" data-sentry-source-file="NextGenerationCarousel.tsx">
              {items.map(carouselItem => <CarouselItem className='desktop:basis-96 flex w-full basis-72 justify-center' key={`next-gen-carousel-item-${carouselItem.setAHeading}`}>
                  <NextGenerationTile handleSelection={handleSelection} item={carouselItem} />
                </CarouselItem>)}
            </CarouselWithDots>
          </div>
        </div>
      </div>
    </>;
}